import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ClipboardList,
  Trash2,
  X,
} from "lucide-react";
import { MailHeaderIconButton } from "@/features/shell/MailHeaderIconButton";
import { MailActivityIndicator } from "@/features/shared/MailActivityIndicator";
import { MailTooltip } from "@/features/shared/MailTooltip";
import { bootstrapMailActivityJournalBridge } from "@/lib/mail/mailActivityJournalBridge";
import { splitMailActivityLabel } from "@/lib/mail/mailActivityJournalNote";
import {
  acknowledgeMailMutationErrors,
  clearFinishedMailMutations,
  ensureMailActivityJournalHydrated,
  getMailMutationHeaderState,
  getMailMutationJournal,
  startOfWeekMondayMs,
  subscribeMailMutationProgress,
  type MailMutationHeaderState,
  type MailMutationJob,
  type MailMutationStep,
} from "@/lib/mail/mailMutationProgress";
import { cn } from "@/lib/cn";

function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatDay(ts: number): string {
  try {
    return new Date(ts).toLocaleDateString("fr-FR", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  } catch {
    return "";
  }
}

function HeaderIcon({ state }: { state: MailMutationHeaderState }) {
  if (state === "busy") {
    return <MailActivityIndicator size="md" label="Traitements en cours" />;
  }
  if (state === "error") {
    return <AlertCircle className="h-4 w-4 text-red-600" strokeWidth={2.25} />;
  }
  return <CheckCircle2 className="h-4 w-4 text-emerald-600" strokeWidth={2.25} />;
}

function headerLabel(state: MailMutationHeaderState): string {
  if (state === "busy") return "Traitements en cours — ouvrir le journal";
  if (state === "error") return "Erreur de traitement — ouvrir le journal";
  return "Journal des traitements — à jour";
}

function StepRow({ step }: { step: MailMutationStep }) {
  return (
    <li className="flex items-center gap-1.5 py-0.5 text-[10px] leading-tight">
      <span className="flex h-2.5 w-2.5 shrink-0 items-center justify-center">
        {step.status === "running" ? (
          <MailActivityIndicator size="xs" label={step.label} />
        ) : step.status === "pending" ? (
          <span
            className="block h-1.5 w-1.5 rounded-full bg-[var(--mail-muted)]/40"
            aria-hidden
          />
        ) : step.status === "ok" ? (
          <Check className="h-2.5 w-2.5 text-emerald-600" strokeWidth={2.5} aria-hidden />
        ) : (
          <AlertCircle className="h-2.5 w-2.5 text-red-600" aria-hidden />
        )}
      </span>
      <span className="min-w-0 flex-1 truncate text-[var(--mail-text)]" title={step.label}>
        {step.label}
      </span>
      {step.error ? (
        <span className="max-w-[40%] shrink-0 truncate text-red-700" title={step.error}>
          {step.error}
        </span>
      ) : null}
    </li>
  );
}

function JobCard({ job }: { job: MailMutationJob }) {
  const busy = job.phase !== "done";
  const showSteps = (job.steps?.length ?? 0) > 0;
  const showProgress =
    job.progress != null &&
    job.progress.total > 1 &&
    (busy || job.progress.done < job.progress.total);
  const progressPercent =
    showProgress && job.progress
      ? Math.min(100, Math.round((job.progress.done / job.progress.total) * 100))
      : null;
  const { title, detail } = splitMailActivityLabel(job.label);

  return (
    <article
      className={cn(
        "rounded-md border px-2 py-1.5",
        job.error
          ? "border-red-200 bg-red-50/70"
          : busy
            ? "border-[var(--mail-accent)]/25 bg-[var(--mail-accent-soft)]/40"
            : "border-[var(--mail-border-soft)] bg-[var(--mail-surface)]"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium leading-snug text-[var(--mail-text)]" title={job.label}>
            {title}
          </p>
          {detail ? (
            <p className="mt-0.5 text-[10px] leading-snug text-[var(--mail-muted)] break-words">
              {detail}
            </p>
          ) : null}
          <p className="mt-0.5 text-[9px] tabular-nums text-[var(--mail-muted)]">
            {formatDay(job.createdAt)} · {formatTime(job.createdAt)}
          </p>
        </div>
        {busy ? (
          <MailActivityIndicator size="sm" className="mt-0.5" label="En cours" />
        ) : job.error ? (
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-red-600" aria-hidden />
        ) : (
          <Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" strokeWidth={2.5} aria-hidden />
        )}
      </div>

      {job.error ? (
        <p className="mt-1 text-[10px] font-medium text-red-700">{job.error}</p>
      ) : null}

      {progressPercent != null && job.progress ? (
        <div className="mt-1.5">
          <div className="mb-0.5 flex justify-between text-[9px] tabular-nums text-[var(--mail-muted)]">
            <span>
              {job.progress.done}/{job.progress.total}
            </span>
            <span>{progressPercent} %</span>
          </div>
          <div className="h-0.5 overflow-hidden rounded-full bg-[var(--mail-surface-hover)]">
            <div
              className="h-full rounded-full bg-[var(--mail-accent)]/80 transition-all"
              style={{ width: `${Math.max(progressPercent, 4)}%` }}
            />
          </div>
        </div>
      ) : busy && !showSteps ? (
        <div className="mt-1.5 h-0.5 overflow-hidden rounded-full bg-[var(--mail-surface-hover)]">
          <div className="inbox-load-progress-indeterminate h-full w-2/5 rounded-full bg-[var(--mail-accent)]/70" />
        </div>
      ) : null}

      {showSteps && job.steps ? (
        <ul className="mt-1.5 max-h-32 space-y-0 overflow-y-auto border-t border-[var(--mail-border-soft)]/80 pt-1">
          {job.steps.map((step) => (
            <StepRow key={step.id} step={step} />
          ))}
        </ul>
      ) : null}
    </article>
  );
}

type MailActivityJournalButtonProps = {
  /**
   * `popover` = desktop (menu sous le bouton).
   * `sheet` = mobile (overlay plein écran).
   */
  presentation?: "popover" | "sheet";
};

/** Bouton header + panneau journal des traitements (optimistic UI / IMAP fond). */
export function MailActivityJournalButton({
  presentation = "popover",
}: MailActivityJournalButtonProps) {
  const sheet = presentation === "sheet";
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState<MailMutationJob[]>(() => {
    ensureMailActivityJournalHydrated();
    return getMailMutationJournal();
  });
  const [headerState, setHeaderState] = useState<MailMutationHeaderState>(() =>
    getMailMutationHeaderState()
  );
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ensureMailActivityJournalHydrated();
    bootstrapMailActivityJournalBridge();
    setJobs(getMailMutationJournal());
    setHeaderState(getMailMutationHeaderState());
    return subscribeMailMutationProgress(() => {
      setJobs(getMailMutationJournal());
      setHeaderState(getMailMutationHeaderState());
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    acknowledgeMailMutationErrors();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    if (sheet) {
      return () => document.removeEventListener("keydown", onKey);
    }
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, sheet]);

  const finishedCount = useMemo(
    () => jobs.filter((job) => job.phase === "done").length,
    [jobs]
  );

  const weekHint = useMemo(() => {
    const start = startOfWeekMondayMs();
    try {
      return `Semaine du ${new Date(start).toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "short",
      })} — purge lundi`;
    } catch {
      return "Historique de la semaine — purge lundi";
    }
  }, []);

  const panel = open ? (
    <div
      role="dialog"
      aria-label="Journal des traitements"
      className={
        sheet
          ? "fixed inset-x-0 bottom-0 top-[max(3.5rem,env(safe-area-inset-top))] z-[80] flex flex-col overflow-hidden border-t border-[var(--mail-border)] bg-[var(--mail-bg)] shadow-2xl"
          : "absolute right-0 top-full z-50 mt-1.5 flex w-[min(22rem,calc(100vw-1.5rem))] max-h-[min(28rem,70vh)] flex-col overflow-hidden rounded-xl border border-[var(--mail-border)] bg-[var(--mail-bg)] shadow-lg"
      }
    >
      <div className="flex items-center gap-2 border-b border-[var(--mail-border-soft)] px-3 py-2">
        <ClipboardList className="h-3.5 w-3.5 text-[var(--mail-muted)]" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-[var(--mail-text)]">
            Journal des traitements
          </p>
          <p className="truncate text-[10px] text-[var(--mail-muted)]">{weekHint}</p>
        </div>
        <button
          type="button"
          className="rounded p-1 text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]"
          aria-label="Fermer"
          onClick={() => setOpen(false)}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-2.5 py-2">
        {jobs.length === 0 ? (
          <p className="px-1 py-6 text-center text-xs text-[var(--mail-muted)]">
            Aucun événement cette semaine — réception, suppressions et sync apparaîtront ici.
          </p>
        ) : (
          jobs.map((job) => <JobCard key={job.id} job={job} />)
        )}
      </div>

      {finishedCount > 0 ? (
        <div className="border-t border-[var(--mail-border-soft)] px-2.5 py-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-medium text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]"
            onClick={() => clearFinishedMailMutations()}
          >
            <Trash2 className="h-3 w-3" aria-hidden />
            Effacer l’historique de la semaine
          </button>
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <div ref={rootRef} className="relative">
      {sheet ? (
        <button
          type="button"
          aria-label={headerLabel(headerState)}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen((v) => !v)}
          className="rounded-full p-2 text-[var(--mail-muted)] transition active:bg-[var(--mail-surface-hover)] active:text-[var(--mail-text)]"
        >
          <span className="relative inline-flex">
            <HeaderIcon state={headerState} />
          </span>
        </button>
      ) : (
        <MailTooltip label={headerLabel(headerState)} side="bottom" compact>
          <MailHeaderIconButton
            label={headerLabel(headerState)}
            aria-expanded={open}
            aria-haspopup="dialog"
            onClick={() => setOpen((v) => !v)}
          >
            <span className="relative inline-flex">
              <HeaderIcon state={headerState} />
              {headerState === "idle" ? (
                <ClipboardList className="sr-only" aria-hidden />
              ) : null}
            </span>
          </MailHeaderIconButton>
        </MailTooltip>
      )}

      {sheet && open ? (
        <button
          type="button"
          aria-label="Fermer le journal"
          className="fixed inset-0 z-[70] bg-black/35"
          onClick={() => setOpen(false)}
        />
      ) : null}
      {panel}
    </div>
  );
}
